import * as RadixHoverCard from '@radix-ui/react-hover-card';
import { type FC, type ReactNode, useMemo } from 'react';
import type { CurrentYearEstimates } from '../../../data/store-sales-stat';
import type { YearSales } from '../../../data/year-sales';
import { combinedCurrencyFormatter } from '../../../helpers/format-currency';
import { longMonthNames } from '../../../helpers/months';
import {
  lastYearColorTailwind,
  thisYearColorTailwind,
  thisYearEstimatesColorTailwind,
} from '../constants';
import type { FormatType, Timespan } from '../types';

export const formatPercentYoY = (percent: number) => {
  const formatted = `${(percent * 100).toFixed(1)}%`;
  if (percent <= 0) {
    return `${formatted}`;
  }

  return `+${formatted}`;
};

export const useImportMonth = (lastImported: Date) => {
  // Use UTC methods throughout: dates are stored as UTC midnight (e.g. "2026-03-01T00:00:00Z").
  // Using local-time getMonth() would shift March 1 UTC → February 28 in EST, causing a
  // mismatch with the backend's month computation and corrupting MTD YoY comparisons.
  const index = useMemo(() => {
    return lastImported.getUTCMonth();
  }, [lastImported]);
  const monthName = useMemo(() => {
    return longMonthNames[lastImported.getUTCMonth()];
  }, [lastImported]);
  // MTD data is meaningful when the import is at most 1 month behind today.
  // Being 1 month behind is normal at the start of a new month before uploads arrive.
  // 2+ months behind means the store hasn't had activity in a while.
  const isMtdCurrent = useMemo(() => {
    const now = new Date();
    const monthsBehind =
      (now.getUTCFullYear() - lastImported.getUTCFullYear()) * 12 +
      (now.getUTCMonth() - lastImported.getUTCMonth());

    return monthsBehind <= 1;
  }, [lastImported]);

  return { index, monthName, isMtdCurrent };
};

const getBackgroundColor = (timespan: Timespan) => {
  switch (timespan) {
    case 'lastYear': {
      return lastYearColorTailwind;
    }

    case 'thisYear': {
      return thisYearColorTailwind;
    }

    case 'thisYearEstimates': {
      return thisYearEstimatesColorTailwind;
    }
  }
};

type SummaryTileProperties = {
  title: string;
  current?: number;
  previous?: number;
  format: FormatType;
  timespan: Timespan;
  tooltipContent?: ReactNode;
};

export const SummaryTile: FC<SummaryTileProperties> = ({
  title,
  current,
  previous,
  format,
  timespan,
  tooltipContent,
}) => {
  const className = useMemo(() => {
    const color = getBackgroundColor(timespan);
    const textColor = timespan === 'thisYearEstimates' ? 'text-gray-600' : 'text-white';

    return `grid grid-rows-[1fr_min-content] ${color} ${textColor} p-2 pt-4 pb-4 font-bold text-xl content-center gap-1`;
  }, [timespan]);

  const stat = useMemo(() => {
    if (current === undefined || Number.isNaN(current)) {
      return 'N/A';
    }

    switch (format) {
      case 'currency': {
        return combinedCurrencyFormatter(current);
      }

      case 'number': {
        return new Intl.NumberFormat('en-US', {
          notation: 'compact',
        }).format(current);
      }

      case 'percentage': {
        return formatPercentYoY(current);
      }
    }
  }, [current, format]);

  const percentChange = useMemo(() => {
    if (!previous || !current) {
      return;
    }

    if (format === 'percentage') {
      return current - previous;
    }

    return (current - previous) / previous;
  }, [current, previous, format]);

  const percentChangeClassName = useMemo(() => {
    const textColor = percentChange !== undefined && percentChange < 0 ? 'text-red-600' : '';

    return `text-base ${textColor}`;
  }, [percentChange]);

  const tileContent = (
    <div className="grid grid-rows-[auto_1fr] text-center">
      {/*
       * The heading shrinks with its card rather than wrapping. Four of these
       * share a row, and Gross Margin's are the longest the dashboard has
       * ("2025 MTD $" … "2026 Total ~"); measured, that row of four needs
       * 322px at 12px, 371px at 14px, 419px at 16px, 468px at 18px and 516px
       * at 20px, px-1 included. Each step below is the measurement for the
       * size it turns on, so the heading is the largest that still fits on one
       * line. A container query measures the container's CONTENT box, so these
       * compare against the card inside its padding — no arithmetic needed.
       * Wrapping is what breaks the tiles' alignment: each tile is its own
       * two-row grid, so one heading taking a second line leaves its
       * neighbours a taller value block.
       *
       * px-1, not p-2: the wider gutter costs 64px across the row, which is a
       * whole size step. The headings are centred over their tiles, so the
       * horizontal padding buys nothing but the wrap.
       */}
      <h3 className="@min-[23.5rem]:text-sm @min-[26.5rem]:text-base @min-[29.5rem]:text-lg @min-[32.5rem]:text-xl text-xs font-bold px-1 py-2 text-gray-600">
        {title}
      </h3>
      <div className={className}>
        <div>{stat}</div>
        {percentChange !== undefined && (
          <div className={percentChangeClassName}>{formatPercentYoY(percentChange)} YoY</div>
        )}
      </div>
    </div>
  );

  if (tooltipContent) {
    // The historical-totals card brings its own chrome (white card, border,
    // shadow), so the hover-card host stays transparent — no wrapper class.
    return (
      <RadixHoverCard.Root openDelay={300} closeDelay={150}>
        <RadixHoverCard.Trigger asChild>{tileContent}</RadixHoverCard.Trigger>
        <RadixHoverCard.Portal>
          <RadixHoverCard.Content
            side="bottom"
            align="center"
            sideOffset={10}
            collisionPadding={8}
            className="z-popover"
          >
            {tooltipContent}
          </RadixHoverCard.Content>
        </RadixHoverCard.Portal>
      </RadixHoverCard.Root>
    );
  }

  return tileContent;
};

export const SummaryHeader = ({ children }: { children: ReactNode }) => (
  <div className="text-lg font-bold pt-1 text-gray-600 text-center border-b border-gray-400">
    {children}
  </div>
);

export type SalesSummaryProperties = {
  lastImported: Date;
  thisYearSales: YearSales;
  thisYearEstimates: CurrentYearEstimates;
  historicalYears: YearSales[];
};

export { AverageOrderValueSummary } from './average-order-value';
export { MarginSummary } from './gross-margin';
export { HistoricalYearsTooltip } from './historical-tooltip';
export { SalesSummary } from './sales';
export { TransactionsSummary } from './transactions';
